import { type Href, router, useFocusEffect } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ActivityRow } from '@/components/ActivityRow';
import { AvailableBalanceCard } from '@/components/AvailableBalanceCard';
import { Avatar } from '@/components/Avatar';
import { BonusPromoCard } from '@/components/BonusPromoCard';
import { Card } from '@/components/Card';
import { FeaturedDealCard } from '@/components/FeaturedDealCard';
import { GiftingHomeCard } from '@/components/gifting/GiftingHomeCard';
import { Heading } from '@/components/Heading';
import { HeroEarningsCard } from '@/components/HeroEarningsCard';
import { MoneyDisplay } from '@/components/MoneyDisplay';
import { Overline } from '@/components/Overline';
import { SectionHeader } from '@/components/SectionHeader';
import { SprintHomeCard } from '@/components/SprintHomeCard';
import { Text as MpText } from '@/components/Text';
import { Toast } from '@/components/Toast';
import { colors, fonts, fontSizes, spacing } from '@/constants/theme';
import { getPushPermission, hasPrimedPush, markPushPrimed } from '@/services/pushNotifications';
import { authStore } from '@/stores/auth.store';
import { dealsStore } from '@/stores/deals.store';
import { earningsStore } from '@/stores/earnings.store';
import { referralStatsStore } from '@/stores/referralStats.store';
import { EARNING_STATUS } from '@/types/earning';

const RECENT_ACTIVITY_LIMIT = 5;

// Module-level (not a per-instance useRef): survives a HomeTab unmount/remount
// within a session, so a remount racing the in-flight async check can't
// double-present the priming modal. hasPrimedPush() is the cross-session guard
// (persisted); this is the intra-session one. Set synchronously before any
// await, so a second focus can't interleave. Reset in tests via the helper below.
let pushPrimePrompted = false;

/** @internal test-only reset for the once-per-session priming guard. */
export function __resetPushPrimePromptForTest() {
  pushPrimePrompted = false;
}

const HomeTab = observer(() => {
  const user = authStore.user;
  const balanceCents = user?.balance_cents ?? 0;
  const cashoutEnabled = user?.cashout_enabled ?? false;
  const initials = (user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '');
  const displayName = user?.first_name && user?.last_name
    ? `${user.first_name} ${user.last_name[0]}.`
    : user?.first_name ?? 'Friend';

  useEffect(() => {
    void earningsStore.ensureLoaded();
    void referralStatsStore.ensureLoaded();
  }, []);

  // Deals revalidate on focus, not just once on mount, because the featured
  // slot below is derived from them: finish a deal on the Deals tab, come
  // back here, and a load-once list would still be pitching the one you just
  // completed. Same pair the Deals tab uses — `refreshList` no-ops until the
  // cold load lands and then throttles itself to one refetch per 30s, so this
  // is rarely an actual request.
  //
  // `refreshMe` rides along for the same reason: the sprint card's referral
  // count lives on the user payload, and so does the server's decision that
  // the contest is over. Without this a long-lived session would sit on a
  // stale count and keep showing the card past Sept 1.
  useFocusEffect(
    useCallback(() => {
      void dealsStore.ensureLoaded();
      void dealsStore.refreshList();
      void authStore.refreshMe().catch(() => undefined);
    }, []),
  );

  // One-time push priming: the first time an authenticated user actually sees
  // home (useFocusEffect fires on focus, not while a deal is stacked on top),
  // present the priming modal — unless they've already been primed or already
  // answered the OS prompt. Native-only; the persisted `primed` flag means a
  // re-mount (backgrounding, tab switches) never re-prompts. This is the sole
  // trigger, so priming can never appear during signup or a deal flow.
  useFocusEffect(
    useCallback(() => {
      if (Platform.OS === 'web' || pushPrimePrompted) return;
      pushPrimePrompted = true;
      void (async () => {
        try {
          if (await hasPrimedPush()) return;
          const status = await getPushPermission();
          if (status !== 'undetermined') {
            await markPushPrimed(); // already answered at the OS level — don't nag
            return;
          }
          router.push('/notifications-priming' as Href);
        } catch (err) {
          if (__DEV__) console.warn('[push] home priming check failed', err);
        }
      })();
    }, []),
  );

  // Recent Activity is a "live feed" of balance-affecting events. Cashed-out
  // earnings are filtered out: today's data model conflates the original
  // earning event with its later cash-out (mutating `status` instead of
  // emitting a separate CashOut event), so a cashed-out row reads as
  // "−$75 SoFi Direct" which is wrong — the SoFi/Direct connection is to
  // how the money came in, not how it left. Until the backend splits
  // BalanceEvent into independent Earning + CashOut tables (see
  // `docs/ROADMAP.md` Phase 6 backlog), the home feed shows only events
  // we can render honestly: available (Earned) + voided (Refunded).
  // The full audit trail including cashed-out rows lives on /earnings.
  const feedEarnings = earningsStore.earnings.filter(
    (e) => e.status !== EARNING_STATUS.CashedOut,
  );
  const recentEarnings = feedEarnings.slice(0, RECENT_ACTIVITY_LIMIT);
  const hasEarnings = feedEarnings.length > 0;

  const lifetimeEarnedCents = user?.lifetime_earned_cents ?? 0;
  const paidReferralsCount = referralStatsStore.data?.totals.paid_referrals_count ?? 0;
  const [toast, setToast] = useState<string | null>(null);

  // The pinned slot can hold both cards. `campaignLive` decides the order —
  // see the render block below. The gift card is built once here rather than
  // written out at both positions, so the two orderings can't drift apart.
  const campaignLive = Boolean(user?.gifting_enabled);
  const giftCard =
    campaignLive || user?.gift_claim ? (
      <GiftingHomeCard claim={user?.gift_claim} onLinkCopied={() => setToast('Link copied')} />
    ) : null;
  const featuredDeal = dealsStore.featuredDeal;

  // End of August Sprint. `typeof`, not truthiness — 0 referrals is exactly who
  // this card is pitching, and the server nils the field when the contest ends,
  // so its absence is the off switch.
  const sprintReferrals = user?.sprint_referrals;
  const sprintLive = typeof sprintReferrals === 'number';

  // Double-payout promo banner (Sept 2026 bonus weeks). Non-null bonus_promo
  // on the deals index is the whole on-switch — the server nils it when the
  // window ends or the kill switch is thrown, so the banner retires with no
  // OTA, exactly like the sprint card's off-switch before it.
  const bonusPromo = dealsStore.bonusPromo;

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 1800);
    return () => clearTimeout(timer);
  }, [toast]);

  return (
    <SafeAreaView style={styles.safe} edges={['top']}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.topBar}>
          <Avatar initials={initials || '?'} size={40} tone="electric" />
          <View style={styles.welcome}>
            <Overline>Welcome back</Overline>
            <Heading size="2xl">{displayName}</Heading>
          </View>
        </View>

        {cashoutEnabled ? (
          <AvailableBalanceCard
            balanceCents={balanceCents}
            lifetimeCents={lifetimeEarnedCents}
            minCents={authStore.minimumCashoutCents}
            onCashOut={() => router.push('/cashout')}
            onHistory={() => router.push('/cashout/history')}
          />
        ) : (
          <HeroEarningsCard
            earnedCents={lifetimeEarnedCents}
            caption="Total rewards from completed deals and referrals."
          />
        )}

        {/* The pinned slot: below the hero, above stats. Season of Gifting
            proved this placement converts, so it now also carries a featured
            deal rather than going dark once a campaign ends.

            A live SPRINT outranks the featured deal and replaces it. Both are
            "do this next" asks in the same voice, and the sprint is the one
            with a deadline and a $1,000 prize on it. The featured deal comes
            back on its own when the server stops sending a count.

            Both cards can show, and `campaignLive` decides which leads. A LIVE
            campaign is an offer, and two offers stacked dilute each other, so
            it takes the top spot. A spent campaign leaves behind a receipt —
            "Gift claimed", view, share — which competes with nothing, so the
            featured deal leads and the receipt sits under it.

            The gift card survives GIFTING_ENABLED being unset because
            `gift_claim` is a DB lookup, not the flag (the claim payload
            carries a product snapshot for exactly this reason). Without the
            both-cards rule, every past claim-holder would sit on a receipt and
            never see a deal until `Gifting::CAMPAIGN` is bumped — which is
            what happens the moment the next season starts, since
            `current_gift_claim` matches on the current campaign only. */}
        {campaignLive && giftCard}
        {/* A live payout boost outranks everything else in the slot — it's
            the offer with a deadline on it. Same slot-priority rule the
            sprint card used against the featured deal. */}
        {bonusPromo ? (
          <BonusPromoCard promo={bonusPromo} testID="home-bonus-promo-card" />
        ) : null}
        {sprintLive && !bonusPromo ? (
          <SprintHomeCard referrals={sprintReferrals} testID="home-sprint-card" />
        ) : null}
        {featuredDeal && !sprintLive && !bonusPromo ? (
          <FeaturedDealCard
            deal={featuredDeal}
            onPress={() => router.push(`/deals/${featuredDeal.slug}`)}
            testID="home-featured-deal"
          />
        ) : null}
        {!campaignLive && giftCard}

        <View style={styles.statsRow}>
          <QuickStat
            label="This month"
            value={<MoneyDisplay cents={earningsStore.monthEarningsCents} size="sm" />}
          />
          <QuickStat
            label="Active deals"
            value={<StatNumber>{dealsStore.activeCount}</StatNumber>}
            accent
          />
          <QuickStat label="Paid referrals" value={<StatNumber>{paidReferralsCount}</StatNumber>} />
        </View>

        <View style={styles.recentActivity}>
          <SectionHeader
            title="Recent Activity"
            actionLabel={hasEarnings ? 'View all →' : undefined}
            onActionPress={hasEarnings ? () => router.push('/earnings') : undefined}
            style={styles.sectionHeader}
          />
          {hasEarnings ? (
            <Card padding={0} style={styles.activityCard}>
              {recentEarnings.map((earning, i) => (
                <ActivityRow
                  key={earning.id}
                  earning={earning}
                  divider={i < recentEarnings.length - 1}
                />
              ))}
            </Card>
          ) : (
            <Card style={styles.activityCard}>
              <MpText variant="caption" color="textMuted" align="center">
                Your earnings show up here once you complete a deal.
              </MpText>
            </Card>
          )}
        </View>
      </ScrollView>

      <Toast message={toast} />
    </SafeAreaView>
  );
});

export default HomeTab;

interface QuickStatProps {
  label: string;
  value: ReactNode;
  accent?: boolean;
}

function QuickStat({ label, value, accent }: QuickStatProps) {
  return (
    <Card variant="flat" style={[styles.statCard, accent && styles.statCardAccent]}>
      <Text style={styles.statLabel}>{label}</Text>
      <View style={styles.statValue}>{value}</View>
    </Card>
  );
}

function StatNumber({ children }: { children: ReactNode }) {
  return <Text style={styles.statNumber}>{children}</Text>;
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: colors.background },
  scroll: { padding: spacing.lg, gap: spacing.lg },
  topBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  welcome: { flex: 1, gap: 2 },
  statsRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  // The row stretches all three tiles to the tallest one, and space-between
  // pins every value to the bottom edge, so a label that wraps ("PAID
  // REFERRALS" at this width) pushes nothing out of line.
  statCard: {
    flex: 1,
    padding: spacing.md,
    gap: spacing.xs,
    justifyContent: 'space-between',
  },
  // Keeps the 1pt border as transparent rather than removing it: dropping
  // the width shifted this tile's content up 1pt against its siblings.
  statCardAccent: {
    backgroundColor: 'rgba(120, 193, 68, 0.18)',
    borderColor: 'transparent',
  },
  statLabel: {
    fontFamily: fonts.bodyBold,
    fontSize: 9,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 1.0,
  },
  statValue: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  statNumber: {
    fontFamily: fonts.numeric,
    fontSize: fontSizes['2xl'],
    color: colors.deepGreen,
    letterSpacing: -0.5,
  },
  recentActivity: {
    // Section is full-bleed left/right because SectionHeader and Card
    // own their own horizontal insets. The parent ScrollView already has
    // padding spacing.lg; we negate it for the section so SectionHeader's
    // own paddingHorizontal lines up with the surrounding content.
    marginHorizontal: -spacing.lg,
    marginTop: spacing.md,
  },
  // Override SectionHeader's default top/bottom margins because the
  // ScrollView already provides `gap: spacing.lg`. Keeps the section
  // sitting flush with the stat row above and the activity card below.
  sectionHeader: {
    marginTop: 0,
    marginBottom: spacing.md,
  },
  // Re-insets the card inside the full-bleed section (see recentActivity).
  // Both the list card and the empty-state card need it, or the empty one
  // runs edge to edge while every other card on the screen keeps the gutter.
  activityCard: {
    marginHorizontal: spacing.lg,
    overflow: 'hidden',
  },
});
